<template>
  <div>
    <SearchableModel
      model-name="Equipment"
      :add-url="addUrl"
      :includes="include"
      load-on-start
      with-search
      with-qr-code-reader
      :search-placeholder="$t('Search by model name, barcode number, serial number, or reference')"
      :push-state="pushHistory"
      :with-export="withExport"
      with-column-picker
      :base-filters="baseFilters"
      :available-filters="availableFilters"
      :enable-container="enableContainer"
      ref="searchableModelRef"
    >
      <template v-if="$slots['header-title']" #header-title>
        <slot name="header-title"></slot>
      </template>

      <template v-if="$slots['header-actions']" #header-actions>
        <slot name="header-actions"></slot>
      </template>

      <template #searchForm="{ search, updateSearch, showScanner }">
        <div class="tw:flex tw:shadow-xs tw:rounded-md">
          <div class="tw:-mr-px tw:flex-1 tw:grid tw:grid-cols-1 tw:focus-within:relative">
            <textarea
              ref="search"
              :value="Array.isArray(search) ? search?.join('\n') : search"
              :rows="numberOfSearchRows(search)"
              @input="updateSearch(formatSearchInput($event))"
              class="tw:col-start-1 tw:row-start-1 tw:pl-10 tw:w-full tw:rounded-l-md tw:outline-1 tw:-outline-offset-1 tw:outline-zinc-300 tw:focus:outline-2 tw:focus:-outline-offset-2 tw:focus:outline-blue-600 tw:px-4 tw:py-2"
              :placeholder="$t('Search by model name, barcode number, serial number, or reference')"
            ></textarea>

            <div class="tw:pointer-events-none tw:col-start-1 tw:row-start-1 tw:self-start tw:mt-2.5 tw:ml-3">
              <Search class="tw:size-5 tw:text-zinc-500" />
            </div>
          </div>
          <button class="tw:flex tw:shrink-0 tw:items-center tw:gap-1.5 tw:px-3 tw:py-2 tw:rounded-r-md tw:hover:bg-zinc-50 tw:outline-1 tw:-outline-offset-1 tw:outline-gray-300 tw:focus:relative tw:focus:outline-2 tw:focus:-outline-offset-2 tw:focus:outline-blue-600 tw:text-sm tw:font-medium" type="button"
                  @click.prevent="showScanner()">
            <ScanQrCode class="tw:w-4 tw:h-4 tw:text-zinc-400" /> Scan
          </button>
        </div>
      </template>
      <template v-slot="{ models, getResults }">
        <slot :equipments="models"
              :equipmentsSelected="equipmentsSelected"
              :getEquipments="getResults"
              :searchableModelRef="searchableModelRef"
              :selectable="selectable || withEquipmentActions"
              :selected="onSelected"
              :deselected="onDeselected"
              :productMetafields="productMetafields"
              :equipmentMetafields="equipmentMetafields"
        ></slot>
      </template>
    </SearchableModel>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import SearchableModel from "@/components/searchable-model/SearchableModel.vue";
import { ScanQrCode, Search } from "@lucide/vue";

// Props
defineProps({
  pushHistory: {
    type: Boolean,
    default: false
  },
  selectable: {
    type: Boolean,
    default: false
  },
  availableFilters: {
    type: Array,
    default: () => []
  },
  equipmentsSelected: {
    type: Array,
    default: () => []
  },
  baseFilters: {
    type: Object,
    default: () => ({})
  },
  withExport: {
    type: Boolean,
    default: false
  },
  allotmentResource: {
    type: String,
    default: ''
  },
  /* metafields are used to display additional information in the list
  (filtering is handled by availableFilters prop) */
  productMetafields: {
    type: Array,
    default: () => []
  },
  equipmentMetafields: {
    type: Array,
    default: () => []
  },
  include: {
    type: Array,
    default: () => []
  },
  addUrl: {
    type: String,
    default: null
  },
  enableContainer: {
    type: Boolean,
    default: false
  },
  withEquipmentActions: {
    type: Boolean,
    default: false
  },
  bulkActionUrl: {
    type: String,
    default: undefined
  },
});

const searchableModelRef = ref(null);

// numberOfSearchRows is used to adapt the textearea height
const numberOfSearchRows = (search) => {
  if (!search || ! Array.isArray(search)) {
    return 1;
  }
  const rows = search.length ?? 1;
  return rows > 8 ? 8 : rows;
};

const formatSearchInput = (e) => {
  return e.target.value
    .replace(/[,;]/g, '\n')
    .split('\n')
    .filter((line, index, lines) => {
      return index === lines.length - 1 || (lines.indexOf(line) === index && line !== '')
    });
};

const emit = defineEmits(['selected', 'deselected']);
const onSelected = (e) => {
  emit('selected', e)
}
const onDeselected = (e) => {
  emit('deselected', e)
}

</script>
